// src/components/ToggleCell.jsx
import React from "react";

/**
 * One matrix cell.
 *
 * Three different kinds of doubt, kept apart on purpose — they came from two
 * independent lines of work and they are not the same claim:
 *
 *  - `!exists`      the tenant was read and has no such flag. An answer.
 *  - `!available`   the tenant could not be read at all, so nothing is known
 *                   about this flag. Not an answer.
 *  - `unconfirmed`  the write was accepted but reading it back failed, so this
 *                   shows what was SENT rather than what the tenant holds.
 *
 * `available` outranks `unconfirmed`: if the tenant cannot be read now, the
 * value that was sent is not worth showing as if it were state.
 */
export default function ToggleCell({
  exists,
  value,
  available = true,
  saving,
  unconfirmed,
  onClick,
}) {
  // "?" alone and "On?"/"Off?" must not be confused: the first says we know
  // nothing, the second says we know what we sent.
  const base = value === null ? "—" : value ? "On" : "Off";
  const label = !available ? "?" : unconfirmed ? `${base}?` : base;

  const stateClass = !available
    ? "ff-cell-unknown"
    : !exists
    ? "ff-cell-na"
    : value
    ? "ff-cell-on"
    : "ff-cell-off";

  const title = !available
    ? "Could not read this organization's database — state unknown"
    : !exists
    ? "Flag does not exist in this org"
    : saving
    ? "Saving..."
    : unconfirmed
    ? "Saved, but could not be read back — showing the value that was sent"
    : "Toggle";

  return (
    <button
      type="button"
      className={`ff-cell ${stateClass} ${saving ? "ff-cell-saving" : ""} ${
        available && unconfirmed ? "ff-cell-unconfirmed" : ""
      }`}
      onClick={onClick}
      disabled={!available || !exists || saving}
      title={title}
    >
      <span className="ff-cell-label">{label}</span>
      {saving && <span className="ff-cell-spinner" aria-hidden>⏳</span>}
    </button>
  );
}
